import styled from "@emotion/styled"
import { breakpoints, layoutPadding } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"

/* The strip height the design draws the full-width variant at — the height side
   of the crops the contained variant expresses as a ratio (1120×504 at lg,
   1200×540 from xl up). See ImageWrapper for why the full-width variant pins
   the height instead of the ratio. */
const FULL_BLEED_HEIGHT_LG = 504
const FULL_BLEED_HEIGHT_XL = 540

/* The full-width variant's own width tier: the breakpoint's grid width less ONE
   Container gutter, so it keeps HALF a gutter of viewport margin per side where
   the contained column keeps a whole one. Same shape as Container (a per-tier
   max-width, centred), one gutter wider — 1200/1320/1560 against the contained
   column's 1120/1200/1200. See ImageWrapper for why. */
const FULL_WIDTH_LG = breakpoints.lg - layoutPadding.desktop
const FULL_WIDTH_XL = breakpoints.xl - layoutPadding.desktopL
const FULL_WIDTH_XXL = breakpoints.xxl - layoutPadding.desktopXl

/* EGDE-592: the block had no vertical rhythm of its own, so consecutive Image
   Blocks (and an Image Block following any other block) rendered edge-to-edge
   with no separation. Matches the 48/64 block padding used by RichContent,
   FAQs, ValuePropositions, ImageGallery and EmployeesTestimonials. */
export const Root = styled.div`
  width: 100%;
  padding: ${sizes.spacing[48]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
  }
`

/* Mirrors HeroBanner: two absolutely-positioned layers toggled by media query
   so the choice is made in CSS (SSR-safe — no viewport hook, no hydration
   flash). `sizes` on each <Image> keeps the hidden layer from being fetched. */
export const DesktopLayer = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const MobileLayer = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const ImageWrapper = styled.div<{
  $borderRadius: boolean
  $contained?: boolean
}>`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => (p.$contained ? "343 / 195" : "1 / 1")};
  overflow: hidden;
  border-radius: ${(p) =>
    p.$borderRadius ? `${sizes.borderRadius[24]}px` : "0"};

  /* EGDE-592: the full-width variant must keep a viewport margin. It may not run
     to the viewport edge (Raz, via QA, three returns) and it may not collapse
     onto the contained column either (r2 capped it to the content column and QA
     answered "there's no difference between FullWidth and Contained now").

     The target screenshot was measured off /why-us at a 1440 viewport, where the
     anchor rail's RailPageContent insets every block by 120px: full-width sat at
     1200px (120px margin), contained at 960px (240px). Half a gutter of margin
     against the contained column's whole one. That only held because of the
     rail, which stops insetting above 1600, so the variant reached the edge
     again on QA's 1920 screen.

     Owning the inset here makes it hold on every page and width: a per-tier
     max-width one gutter wider than the contained column, centred, so it also
     stops growing past xxl instead of stretching a 1200px asset across an
     ultra-wide screen. Mobile stays edge-to-edge, as the design draws it.
     The object-fit:cover on the Image crops the extra width. */
  @media (min-width: ${breakpoints.lg}px) {
    border-radius: ${(p) =>
      p.$borderRadius ? `${sizes.borderRadius[40]}px` : "0"};
    ${(p) =>
      p.$contained
        ? "aspect-ratio: 1120 / 540;"
        : `aspect-ratio: auto;
           height: ${FULL_BLEED_HEIGHT_LG}px;
           max-width: ${FULL_WIDTH_LG}px;
           margin-inline: auto;`}
  }

  @media (min-width: ${breakpoints.xl}px) {
    ${(p) =>
      p.$contained
        ? "aspect-ratio: 1200 / 540;"
        : `height: ${FULL_BLEED_HEIGHT_XL}px;
           max-width: ${FULL_WIDTH_XL}px;`}
  }

  @media (min-width: ${breakpoints.xxl}px) {
    ${(p) => (p.$contained ? "" : `max-width: ${FULL_WIDTH_XXL}px;`)}
  }
`
